import { useQueries, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { CalendarClock, ChevronDown, ChevronLeft, ChevronRight, Users } from 'lucide-react'
import { Popover } from 'radix-ui'
import { useEffect, useEffectEvent, useState } from 'react'
import {
  classTypesQuery,
  providersQuery,
  resourcesQuery,
  sessionsQuery,
  timeOffQuery,
  workingHoursQuery,
  type Provider,
  type Session,
} from '@/api/services'
import { photoSrc } from '@/api/queries'
import { useNow } from '@/app/access/use-now'
import { AppointmentPanel, type AppointmentDraft } from '@/app/services/appointment-panel'
import { BookingPanel } from '@/app/services/booking-panel'
import { SessionPanel } from '@/app/services/session-panel'
import { nobodyOption, personOption } from '@/app/services/person-option'
import { BranchSelect, ServicesShell, ToolbarSelect, type ServicesContext } from '@/app/services/services-shell'
import {
  addDays,
  blockOnDay,
  branchForSlot,
  countByDate,
  dayLabel,
  dayPeople,
  hhmm,
  minutesOf,
  placesMeta,
  visibleHours,
  wallOf,
  weekDays,
  weekdayOf,
  weekLabel,
  weekRange,
  weekStart,
} from '@/domain/calendar'
import { errorText } from '@/domain/errors'
import { t } from '@/i18n'
import { cn } from '@/lib/cn'
import { Button, IconButton } from '@/ui/button'
import { Checkbox } from '@/ui/checkbox'
import { Calendar as MiniMonth } from '@/ui/date-field'
import { InlineBanner } from '@/ui/inline-banner'
import { EmptyState } from '@/ui/page'
import { NeutralSegmented } from '@/ui/segmented'
import { DateHead, GridSkeleton, PersonHead, WeekGrid, type GridColumn, type GridItem, type Interval } from '@/ui/week-grid'

type View = 'day' | 'week'
type Search = { view?: View; date?: string; provider?: string; branch?: string; cancelled?: '1' }

const isDate = (value: unknown): value is string => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
const isId = (value: unknown): value is string => typeof value === 'string' && /^[0-9a-f-]{36}$/.test(value)
const isView = (value: unknown): value is View => value === 'day' || value === 'week'

export const Route = createFileRoute('/_app/c/$orgId/services/')({
  validateSearch: (search: Record<string, unknown>): Search => {
    // `week` is the old link's Monday — it still opens that week.
    const date = isDate(search['date']) ? search['date'] : isDate(search['week']) ? search['week'] : undefined
    return {
      ...(isView(search['view']) ? { view: search['view'] } : {}),
      ...(date ? { date } : {}),
      ...(isId(search['provider']) ? { provider: search['provider'] } : {}),
      ...(isId(search['branch']) ? { branch: search['branch'] } : {}),
      // Cancelled sessions are hidden unless asked for.
      ...(search['cancelled'] === '1' || search['cancelled'] === 1 ? { cancelled: '1' as const } : {}),
    }
  },
  component: Calendar,
})

function Calendar() {
  const { orgId } = Route.useParams()
  return (
    <ServicesShell orgId={orgId} tab="calendar">
      {(ctx) => <CalendarScreen ctx={ctx} />}
    </ServicesShell>
  )
}

/// Narrower than this, a week's seven columns are slivers: the calendar
/// opens on a day instead.
const WEEK_FITS_PX = 900
/// A person's column in the day view: a name and a two-line block fit.
const PERSON_COLUMN_PX = 160
/// The day view's column for sessions nobody runs.
const NOBODY = ''

/// The calendar's keys, by physical key (`event.code`): a Ukrainian layout
/// types «е» on T, and the desk should not have to switch layouts.
const KEYS = { today: 'T', prev: 'J', next: 'K', day: 'D', week: 'W', jump: 'G' } as const

/// Whether a key press belongs to something else: a field, a list or a
/// dialog that is open, a key already handled (the grid's arrows), or a
/// chord with a modifier.
const notOurs = (event: KeyboardEvent): boolean => {
  const target = event.target as HTMLElement | null
  return (
    event.defaultPrevented ||
    event.metaKey ||
    event.ctrlKey ||
    event.altKey ||
    Boolean(
      target?.closest(
        'input, textarea, select, [contenteditable=true], [role=dialog], [role=alertdialog], [role=listbox], [role=menu]',
      ),
    )
  )
}

/// Whether the element is narrower than `px` — the CONTENT area, not the
/// window: a 1024 px laptop with the sidebar open has ~700 px for the grid,
/// which a media query on the window cannot tell. Null until measured. A
/// callback ref: the loading state and the grid are different elements.
const useNarrowerThan = (px: number): [(element: HTMLDivElement | null) => void, boolean | null] => {
  const [element, setElement] = useState<HTMLDivElement | null>(null)
  const [narrow, setNarrow] = useState<boolean | null>(null)
  useEffect(() => {
    if (!element) return
    // The first callback comes right after `observe`, before the next paint.
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setNarrow(entry.contentRect.width < px)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [element, px])
  return [setElement, narrow]
}

/// «Календар»: appointments and group sessions in one club on the club's wall
/// clock, buffers and time off hatched. Two views. The DAY — one column per
/// performer, each with their working hours shaded and time off hatched —
/// answers the desk's question: who is free now, and today. The WEEK — seven
/// days, everybody in each — shows one performer's week. An empty cell books
/// an appointment (`sell`); a block opens its bookings. The view, the date,
/// the provider and the club live in the URL, so a link shows the same thing.
function CalendarScreen({ ctx }: { ctx: ServicesContext }) {
  const { orgId } = ctx
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const c = t.services.calendar
  const now = useNow(60_000)
  const [rootRef, narrow] = useNarrowerThan(WEEK_FITS_PX)

  const branchId = search.branch && ctx.branches.some((b) => b.id === search.branch) ? search.branch : null
  const zone = ctx.zoneOf(branchId)
  const today = wallOf(now, zone).date
  // No view in the link: a day where a week would be slivers.
  const view: View = search.view ?? (narrow ? 'day' : 'week')
  const date = search.date ?? today
  const monday = weekStart(date)
  const days = weekDays(monday)
  // The day view asks for its whole week too: moving within it is instant.
  const range = weekRange(monday, zone)
  // `own` sees and books only their own time.
  const providerId = ctx.can.own ? ctx.access.userId : (search.provider ?? null)

  const types = useQuery(classTypesQuery(orgId))
  const personal = (types.data ?? []).filter((x) => x.kind === 'personal' && x.status === 'active')
  const groupServices = (types.data ?? []).filter((x) => x.kind === 'group' && x.status === 'active')
  const resources = useQuery({ ...resourcesQuery(orgId), enabled: ctx.can.manageTimetable })
  const providerLists = useQueries({ queries: personal.map((x) => providersQuery(orgId, x.id)) })
  const sessions = useQuery(
    sessionsQuery(orgId, range.from.toISOString(), new Date(range.to.getTime() - 1).toISOString()),
  )

  const [draft, setDraft] = useState<AppointmentDraft | null>(null)
  /// What an empty cell makes: a provider's appointment or a one-off class.
  const [entry, setEntry] = useState<'appointment' | 'group'>('appointment')
  const [openId, setOpenId] = useState<string | null>(null)
  const [news, setNews] = useState<string | null>(null)
  const [jumping, setJumping] = useState(false)

  const providers: readonly Provider[] = providerLists.flatMap((query) => query.data ?? []).filter((p) => p.status === 'active')
  const providersOf = (classTypeId: string) => providers.filter((p) => p.classTypeId === classTypeId)
  const typeOf = new Map((types.data ?? []).map((x) => [x.id, x]))

  // Cancelled sessions only with «Показати скасовані»: nobody comes to them,
  // and drawn they are faint, behind the live ones.
  const withCancelled = search.cancelled === '1'
  const shown = (sessions.data ?? []).filter(
    (session) =>
      (branchId === null || session.branchId === branchId) &&
      (providerId === null || session.trainerUserId === providerId) &&
      (withCancelled || session.status !== 'cancelled'),
  )
  const ofDay = shown.filter((session) => blockOnDay(session, date, zone) !== null)

  // Who can be filtered by: everybody who provides a service or runs a
  // session this week.
  const people = [
    ...new Set([
      ...providers.map((p) => p.userId),
      ...(sessions.data ?? []).flatMap((session) => (session.trainerUserId ? [session.trainerUserId] : [])),
    ]),
  ].sort((a, b) => ctx.nameOf(a).localeCompare(ctx.nameOf(b), 'uk'))

  // The day's columns: everybody with a service or a session that day — or
  // the one performer picked. Never none: a club with no performers yet
  // still gets a column to put a class in.
  const dayColumnsOf = providerId !== null ? [providerId] : dayPeople(providers.map((p) => p.userId), ofDay, ctx.nameOf)
  const persons: readonly (string | null)[] = view === 'day' ? (dayColumnsOf.length > 0 ? dayColumnsOf : [null]) : []
  // Whose hours and time off the grid shades: the day's people, or the
  // week's one performer.
  const watched = view === 'day' ? persons.filter((id): id is string => id !== null) : providerId ? [providerId] : []
  const hoursQueries = useQueries({ queries: watched.map((id) => workingHoursQuery(orgId, id)) })
  const awayQueries = useQueries({ queries: watched.map((id) => timeOffQuery(orgId, id, range.from.toISOString())) })
  const hoursOf = new Map(watched.map((id, index) => [id, hoursQueries[index]?.data]))

  // A performer's working time on a date: their intervals in this club (or
  // every club, when none is chosen) on that weekday; undefined until known.
  const workingOf = (userId: string | null, on: string): readonly Interval[] | undefined => {
    const hours = userId ? hoursOf.get(userId) : undefined
    return hours
      ?.filter((h) => h.weekday === weekdayOf(on) && (branchId === null || h.branchId === branchId))
      .map((h) => ({ start: minutesOf(h.startLocal), end: minutesOf(h.endLocal) }))
  }

  const away = watched.flatMap((userId, index) => (awayQueries[index]?.data ?? []).map((off) => ({ userId, off })))
  // Which column a block sits in, in the day view: its performer's.
  const ownerOf = new Map<string, string>([
    ...shown.map((session): [string, string] => [session.id, session.trainerUserId ?? NOBODY]),
    ...away.map(({ userId, off }): [string, string] => [`off-${off.id}`, userId]),
  ])
  const items: GridItem[] = [
    ...shown.map((session) => sessionItem(session, typeOf.get(session.classTypeId), ctx, view === 'week' && providerId === null, zone)),
    ...away.map(
      ({ off }): GridItem => ({
        id: `off-${off.id}`,
        startsAt: off.startsAt,
        endsAt: off.endsAt,
        title: off.note || c.timeOff,
        tone: 'off',
        label: `${c.timeOff}: ${off.note ?? ''}`,
      }),
    ),
  ]

  // The week's load per day, for its heads: what the grid shows, once each.
  const counts = countByDate(
    shown.map((session) => ({ startsAt: session.startsAt, cancelled: session.status === 'cancelled' })),
    days,
    zone,
  )

  const longDate = (on: string) =>
    new Intl.DateTimeFormat('uk-UA', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' }).format(
      new Date(`${on}T00:00:00Z`),
    )

  const columns: readonly GridColumn[] =
    view === 'day'
      ? persons.map((userId) => ({
          key: userId ?? NOBODY,
          date,
          head: userId ? (
            <PersonHead
              name={ctx.nameOf(userId)}
              src={photoSrc(ctx.members.find((m) => m.userId === userId)?.photoUrl)}
            />
          ) : (
            <span aria-hidden="true" className="truncate text-caption font-medium text-muted">
              {c.nobody}
            </span>
          ),
          working: workingOf(userId, date),
        }))
      : days.map((day) => {
          const count = counts.get(day) ?? 0
          return {
            key: day,
            date: day,
            head: (
              <DateHead
                date={day}
                today={today}
                note={count > 0 ? c.entries(count) : ''}
                onOpen={() => go({ view: 'day', date: day })}
                openLabel={count > 0 ? c.openDayWith(longDate(day), c.entries(count)) : c.openDay(longDate(day))}
              />
            ),
            working: providerId ? workingOf(providerId, day) : undefined,
          }
        })
  const columnOf = view === 'day' ? (item: GridItem) => ownerOf.get(item.id) ?? NOBODY : undefined

  const spans = columns.flatMap((column) => [
    ...items.flatMap((item) => {
      if (columnOf && columnOf(item) !== column.key) return []
      const block = blockOnDay(item, column.date, zone)
      return block ? [{ start: block.start, end: block.busyEnd }] : []
    }),
    ...(column.working ?? []),
  ])
  const visible = visibleHours(spans)

  function go(next: Partial<Record<keyof Search, string | undefined>>) {
    void navigate({
      search: (prev: Search) => {
        const merged = { ...prev, ...next }
        return Object.fromEntries(Object.entries(merged).filter(([, value]) => value)) as Search
      },
      replace: true,
    })
  }

  const step = view === 'day' ? 1 : 7
  const atToday = view === 'day' ? date === today : monday === weekStart(today)
  const prevLabel = view === 'day' ? c.prevDay : c.prevWeek
  const nextLabel = view === 'day' ? c.nextDay : c.nextWeek
  // A panel over the calendar owns the keyboard: no period moves under it.
  const panelOpen = draft !== null || openId !== null

  // Keyboard first: T, J K, D, W, G — anywhere on the screen but a field,
  // an open list or a panel. ← → move the period too, but only when nothing
  // has focus: on a block, a link or a button an arrow is that control's,
  // and a period changed under it would drop the focus to the page. Read
  // through an effect event, so the listener is added once and still sees
  // this render's date and view.
  const onShortcut = useEffectEvent((event: KeyboardEvent) => {
    if (panelOpen || notOurs(event)) return
    const loose = event.target === document.body || event.target === document.documentElement
    const action =
      event.code === `Key${KEYS.today}`
        ? () => go({ date: undefined })
        : (loose && event.key === 'ArrowLeft') || event.code === `Key${KEYS.prev}`
          ? () => go({ date: addDays(date, -step) })
          : (loose && event.key === 'ArrowRight') || event.code === `Key${KEYS.next}`
            ? () => go({ date: addDays(date, step) })
            : event.code === `Key${KEYS.day}`
              ? () => go({ view: 'day' })
              : event.code === `Key${KEYS.week}`
                ? () => go({ view: 'week' })
                : event.code === `Key${KEYS.jump}`
                  ? () => setJumping(true)
                  : null
    if (!action) return
    event.preventDefault()
    action()
  })
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => onShortcut(event)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const toolbar = (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-1">
        <IconButton
          label={prevLabel}
          title={c.withKey(prevLabel, '← J')}
          aria-keyshortcuts={`ArrowLeft ${KEYS.prev}`}
          onClick={() => go({ date: addDays(date, -step) })}
        >
          <ChevronLeft className="size-5" />
        </IconButton>
        <IconButton
          label={nextLabel}
          title={c.withKey(nextLabel, '→ K')}
          aria-keyshortcuts={`ArrowRight ${KEYS.next}`}
          onClick={() => go({ date: addDays(date, step) })}
        >
          <ChevronRight className="size-5" />
        </IconButton>
        <Button
          variant="secondary"
          compact
          title={c.withKey(c.today, KEYS.today)}
          aria-keyshortcuts={KEYS.today}
          onClick={() => go({ date: undefined })}
          disabled={atToday}
        >
          {c.today}
        </Button>
      </div>
      {/* The period's name is the way to any date: a month in a popover. */}
      <h2 className="mr-auto min-w-0">
        <Popover.Root open={jumping} onOpenChange={setJumping}>
          <Popover.Trigger
            title={c.withKey(c.jump, KEYS.jump)}
            aria-keyshortcuts={KEYS.jump}
            className={cn(
              'inline-flex h-11 max-w-full items-center gap-1.5 rounded-[12px] px-2.5 text-callout font-semibold text-text tabular sm:h-10',
              'transition-colors hover:bg-surface data-[state=open]:bg-surface',
              'outline-none focus-visible:ring-2 focus-visible:ring-border-strong',
            )}
          >
            <span className="truncate" aria-live="polite">
              {view === 'day' ? dayLabel(date) : weekLabel(monday)}
            </span>
            <span className="sr-only">, {c.jump.toLowerCase()}</span>
            <ChevronDown aria-hidden="true" className="size-4 shrink-0 text-muted" />
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              align="start"
              sideOffset={6}
              collisionPadding={12}
              aria-label={c.jump}
              className="popover-in z-50 rounded-[14px] bg-surface shadow-e3 ring-1 ring-surface-3 outline-none"
            >
              <MiniMonth
                // A new period opens on its own month.
                key={date}
                value={date}
                span={view === 'week' ? { from: monday, to: addDays(monday, 6) } : undefined}
                onChange={(picked) => {
                  setJumping(false)
                  if (picked) go({ date: picked === today ? undefined : picked })
                }}
              />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </h2>
      <NeutralSegmented
        label={c.views.label}
        value={view}
        options={(['day', 'week'] as const).map((value) => ({
          value,
          label: c.views[value],
          shortcut: value === 'day' ? KEYS.day : KEYS.week,
        }))}
        onChange={(value) => go({ view: value })}
      />
      {ctx.can.own ? null : (
        <ToolbarSelect
          label={t.services.provider}
          value={providerId ?? ''}
          onChange={(value) => go({ provider: value || undefined })}
          options={[
            nobodyOption(t.services.allProviders),
            ...people.map((userId) => personOption(ctx, userId)),
          ]}
        />
      )}
      <BranchSelect branches={ctx.branches} value={branchId} onChange={(id) => go({ branch: id ?? undefined })} all />
      <Checkbox checked={withCancelled} onCheckedChange={(on) => go({ cancelled: on ? '1' : undefined })} className="px-1">
        {c.showCancelled}
      </Checkbox>
    </div>
  )

  const hoursHint =
    view === 'week' && providerId === null && !ctx.can.own ? (
      <p className="text-caption text-muted">{c.pickProviderForHours}</p>
    ) : null

  if (types.isPending || (sessions.isPending && !sessions.data)) {
    return (
      <div ref={rootRef} className="flex flex-col gap-4">
        {toolbar}
        {hoursHint}
        <GridSkeleton
          columns={view === 'day' ? 3 : 7}
          minColumnPx={view === 'day' ? PERSON_COLUMN_PX : undefined}
          label={t.app.loading}
        />
      </div>
    )
  }
  const failed = types.error ?? sessions.error
  if (failed) {
    return (
      <div ref={rootRef} className="flex flex-col gap-4">
        {toolbar}
        <InlineBanner
          kind="error"
          message={errorText(failed)}
          action={{
            label: t.app.retry,
            onClick: () => {
              void types.refetch()
              void sessions.refetch()
            },
          }}
        />
      </div>
    )
  }

  if (personal.length === 0 && groupServices.length === 0 && (sessions.data ?? []).length === 0) {
    return (
      <EmptyState
        icon={<CalendarClock />}
        title={c.noServices}
        message={c.noServicesMessage}
        action={
          ctx.can.manageCatalogue ? (
            <Link
              to="/c/$orgId/services/catalogue"
              params={{ orgId }}
              className="inline-flex h-11 items-center rounded-[12px] bg-accent px-4.5 text-callout font-semibold text-ink sm:h-10"
            >
              {t.services.catalogue.add}
            </Link>
          ) : undefined
        }
      />
    )
  }

  const bookable = ctx.can.sell && personal.some((x) => providersOf(x.id).length > 0)
  // A one-off group class: whoever writes the timetable (§4.1).
  const schedulable = ctx.can.manageTimetable && groupServices.length > 0
  const pickable = bookable || schedulable
  const switcher =
    bookable && schedulable ? (
      <div className="flex flex-col gap-1.5">
        <span className="text-caption font-medium text-text-2">{t.services.session.kind}</span>
        <NeutralSegmented
          label={t.services.session.kind}
          onSurface
          className="self-start"
          value={entry}
          options={(['appointment', 'group'] as const).map((kind) => ({ value: kind, label: t.services.session.kinds[kind] }))}
          onChange={setEntry}
        />
      </div>
    ) : undefined
  const open = openId ? (sessions.data ?? []).find((session) => session.id === openId) ?? null : null
  const nothing = view === 'day' ? ofDay.length === 0 : shown.length === 0

  return (
    <div ref={rootRef} className={`flex flex-col gap-4 ${sessions.isPlaceholderData ? 'opacity-60 transition-opacity' : ''}`}>
      {toolbar}
      {news ? <InlineBanner kind="done" message={news} /> : null}
      {hoursHint}

      <WeekGrid
        // A new view is a new grid: the roving cell starts over.
        key={view}
        columns={columns}
        columnOf={columnOf}
        minColumnPx={view === 'day' ? PERSON_COLUMN_PX : undefined}
        timeZone={zone}
        hours={visible}
        items={items}
        today={today}
        nowMinutes={wallOf(now, zone).minutes}
        label={view === 'day' ? c.gridDay(dayLabel(date)) : c.grid(weekLabel(monday))}
        hint={c.gridHint}
        settled={!sessions.isPlaceholderData}
        empty={
          nothing && !sessions.isPlaceholderData ? (
            <div className="flex max-w-xs flex-col items-center gap-1.5 rounded-[14px] bg-surface px-5 py-4 text-center shadow-e2 ring-1 ring-surface-3">
              <CalendarClock aria-hidden="true" className="size-5 text-muted" />
              <p className="text-callout font-semibold text-text">{view === 'day' ? c.emptyDay : c.empty}</p>
              <p className="text-caption text-muted">{pickable ? c.emptyMessage : c.emptyReadOnly}</p>
            </div>
          ) : undefined
        }
        cellLabel={(column, minutes, past) =>
          (past ? c.cellPast : pickable ? c.cell : c.cellReadOnly)(
            view === 'day' ? (column.key ? ctx.nameOf(column.key) : c.nobody) : longDate(column.date),
            hhmm(minutes),
          )
        }
        onPick={
          pickable
            ? (column, minutes) => {
                setNews(null)
                setEntry(bookable ? 'appointment' : 'group')
                const who = view === 'day' ? column.key || null : providerId
                // On «Усі клуби», the club the provider works in at that time.
                const branch =
                  branchId ??
                  branchForSlot(
                    (who ? hoursOf.get(who) : undefined) ?? [],
                    column.date,
                    minutes,
                    ctx.branches.map((b) => b.id),
                    ctx.branches[0]?.id ?? null,
                  )
                setDraft({ date: column.date, minutes, providerUserId: who, branchId: branch })
              }
            : undefined
        }
        onOpen={(id) => {
          if (!id.startsWith('off-')) setOpenId(id)
        }}
      />
      {/* What the fills mean. Hidden from assistive tech: each block and cell says it in words. */}
      <div aria-hidden="true" className="flex flex-wrap gap-x-5 gap-y-1 text-caption text-muted">
        <span className="inline-flex items-center gap-1.5">
          <span className="relative inline-block size-3 overflow-hidden rounded-[3px] bg-[color-mix(in_srgb,var(--accent)_18%,var(--surface))]">
            <span className="absolute inset-y-0 left-0 w-[3px] bg-accent" />
          </span>
          {c.legendPersonal}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Users className="size-3.5 text-text-2" strokeWidth={2.25} />
          {c.legendGroup}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="hatch inline-block size-3 rounded-[3px] bg-surface" />
          {c.legendBuffer}
        </span>
        {columns.some((column) => column.working) ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block size-3 rounded-[3px] bg-surface-2 shadow-[inset_0_0_0_1px_var(--surface-3)]" />
            {c.legendOff}
          </span>
        ) : null}
        {withCancelled ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block size-3 rounded-[3px] border border-dashed border-border-strong" />
            {c.legendCancelled}
          </span>
        ) : null}
        <span className="sm:hidden">{c.scrollHint}</span>
      </div>

      <AppointmentPanel
        ctx={ctx}
        draft={entry === 'appointment' ? draft : null}
        services={personal}
        providersOf={providersOf}
        switcher={switcher}
        onClose={() => setDraft(null)}
        onBooked={(message) => {
          setDraft(null)
          setNews(message)
        }}
      />
      <SessionPanel
        ctx={ctx}
        draft={entry === 'group' ? draft : null}
        services={groupServices}
        resources={resources.data ?? []}
        switcher={switcher}
        onClose={() => setDraft(null)}
        onCreated={(message) => {
          setDraft(null)
          setNews(message)
        }}
      />
      <BookingPanel
        // A block of its own: the steps and the news of one class never
        // carry over to the next one opened.
        key={open?.id ?? 'closed'}
        ctx={ctx}
        session={open}
        service={open ? typeOf.get(open.classTypeId) : undefined}
        weekSessions={sessions.data ?? []}
        serviceName={(classTypeId) => typeOf.get(classTypeId)?.name ?? t.services.unknownService}
        onClose={() => setOpenId(null)}
        // A class just called off stays on the grid, struck through — the
        // desk sees what it did, and where its refunds are.
        onCancelled={() => (withCancelled ? undefined : go({ cancelled: '1' }))}
      />
    </div>
  )
}

/// One session as a block: the service, who (when the grid shows everybody)
/// or the places taken, and the words a screen reader hears.
const sessionItem = (
  session: Session,
  type: ReturnType<Map<string, import('@/api/services').ClassType>['get']>,
  ctx: ServicesContext,
  showWho: boolean,
  zone: string,
): GridItem => {
  const c = t.services.calendar
  const name = type?.name ?? t.services.unknownService
  const who = ctx.nameOf(session.trainerUserId)
  const cancelled = session.status === 'cancelled'
  const start = hhmm(wallOf(new Date(session.startsAt), zone).minutes)
  const end = hhmm(wallOf(new Date(session.endsAt), zone).minutes)
  const buffer =
    session.busyUntil && Date.parse(session.busyUntil) > Date.parse(session.endsAt)
      ? Math.round((Date.parse(session.busyUntil) - Date.parse(session.endsAt)) / 60_000)
      : 0
  return {
    id: session.id,
    startsAt: session.startsAt,
    endsAt: session.endsAt,
    busyUntil: cancelled ? null : session.busyUntil,
    title: name,
    // A column per person, or one performer picked: the name is already there.
    detail: showWho ? who : undefined,
    // The number booked is not on a session (yet): the capacity alone.
    meta: session.appointment ? undefined : placesMeta(session.capacity),
    tone: cancelled ? 'cancelled' : session.appointment ? 'personal' : 'group',
    colour: type?.colour ?? null,
    label: [
      name,
      `${start}–${end}`,
      who,
      session.appointment ? null : c.placesUpTo(session.capacity),
      cancelled ? c.cancelled : null,
    ]
      .filter(Boolean)
      .join(', '),
    bufferLabel: buffer > 0 ? c.buffer(buffer) : undefined,
  }
}
